export const PUSH_PREFERENCE_EVENT = 'pm:push-preference-changed';

export interface PushPreferenceChange {
  userId: string;
  enabled: boolean;
}

const PUSH_PREFERENCE_KEY_PREFIX = 'pm-push-preference:';
const LEGACY_PUSH_PREFERENCE_KEY_PREFIX = 'hr-push-preference:';

function preferenceKey(userId: string): string {
  return `${PUSH_PREFERENCE_KEY_PREFIX}${userId}`;
}

/**
 * Return the last server-confirmed preference for one user. A missing value is
 * deliberately `null`: native registration must wait for the server instead of
 * briefly opting an account in with another account's cached value.
 */
export function readPushPreference(userId: string): boolean | null {
  if (typeof window === 'undefined' || !userId) return null;

  try {
    let stored = localStorage.getItem(preferenceKey(userId));
    if (stored === null) {
      const legacyKey = `${LEGACY_PUSH_PREFERENCE_KEY_PREFIX}${userId}`;
      stored = localStorage.getItem(legacyKey);
      if (stored !== null) localStorage.setItem(preferenceKey(userId), stored);
      localStorage.removeItem(legacyKey);
    }
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

export function writePushPreference(userId: string, enabled: boolean): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    localStorage.setItem(preferenceKey(userId), String(enabled));
    localStorage.removeItem(`${LEGACY_PUSH_PREFERENCE_KEY_PREFIX}${userId}`);
  } catch {
    // Storage may be unavailable; the in-memory event still updates this session.
  }
}

export function notifyPushPreferenceChanged(userId: string, enabled: boolean): void {
  if (typeof window === 'undefined' || !userId) return;
  writePushPreference(userId, enabled);
  window.dispatchEvent(
    new CustomEvent<PushPreferenceChange>(PUSH_PREFERENCE_EVENT, {
      detail: { userId, enabled },
    }),
  );
}
